/**
 * Modelli TypeScript per JobOrder
 * Le proprietà sono in PascalCase per corrispondere alla serializzazione JSON del backend
 */

import { z } from 'zod';
import type { BaseEntity } from './common';
import type { Customer } from './customer';
import type { Task } from './task';
import type { PurchaseOrder } from './purchaseOrder';
import type { JobOrderJobActivity } from './jobOrderJobActivity';
import type { JobOrderManager } from './jobOrderManager';
import type { JobOrderRequirement } from './jobOrderRequirement';
import type { JobOrderAllowedWorker } from './jobOrderAllowedWorker';
import type { JobOrderAllowedGroup } from './jobOrderAllowedGroup';
import type { JobOrderRevenue } from './jobOrderRevenue';
import type { JobOrderAllowedGroupWorkerView } from './jobOrderAllowedGroupWorkerView';
import type { TimecardEntry } from './timecardEntry';

/**
 * JobOrder - Ordine di lavoro
 * Basata sulla classe C# JobOrder (serializzata in PascalCase)
 */
export interface JobOrder extends BaseEntity {
	// Proprietà scalari principali
	orderType?: number; // Può essere undefined in alcuni contesti
	taskId?: number; // nullable
	code: string; // Required, MaxLength(255)
	name: string; // Required
	customerId?: number; // Può essere undefined in alcuni contesti
	purchaseOrderId?: number; // nullable
	startDate?: string; // DateTime nullable serializzato come string
	endDate?: string; // DateTime nullable serializzato come string
	budgetHours?: number; // decimal(18,4) nullable - Range(0.0, double.MaxValue)
	approvedHours?: number; // decimal(18,4) nullable - Range(0.0, double.MaxValue)
	notes?: string; // nullable
	isRequirementRequired?: boolean; // Può essere undefined in alcuni contesti
	isHolidayJobOrder?: boolean; // Può essere undefined in alcuni contesti

	// Navigation properties singole (opzionali per $expand OData)
	customer?: Customer; // Navigation property per Customer
	task?: Task; // Navigation property per Task
	purchaseOrder?: PurchaseOrder; // Navigation property per PurchaseOrder

	// Navigation properties collezioni (opzionali per $expand OData)
	activities?: JobOrderJobActivity[]; // IList<JobOrderJobActivity>
	managers?: JobOrderManager[]; // IList<JobOrderManager>
	revenues?: JobOrderRevenue[]; // IList<JobOrderRevenue>
	requirements?: JobOrderRequirement[]; // IList<JobOrderRequirement>
	allowedWorkers?: JobOrderAllowedWorker[]; // IList<JobOrderAllowedWorker>
	allowedGroupWorkers?: JobOrderAllowedGroupWorkerView[]; // IList<JobOrderAllowedGroupWorkerView>
	allowedGroups?: JobOrderAllowedGroup[]; // IList<JobOrderAllowedGroup>
	timecardEntries?: TimecardEntry[]; // IList<TimecardEntry>
}

/**
 * Tipo per il risultato dell'autocomplete JobOrder
 * Corrisponde ai campi selezionati nella query OData: id, code, name, isRequirementRequired + expand customer (id, code, name)
 */
export type JobOrderAutocompleteResult = Pick<
	JobOrder,
	'id' | 'code' | 'name' | 'isRequirementRequired'
> & {
	/** Cliente associato (expandato) */
	customer?: {
		/** ID del cliente */
		id: number;
		/** Codice del cliente */
		code: string;
		/** Nome del cliente */
		name: string;
	};
};

/**
 * CopyOrderParameters - Parametri per la copia di un JobOrder
 * Corrisponde al modello backend CopyOrderParameters
 */
export interface CopyOrderParameters {
	/** ID dell'ordine da copiare */
	orderId: number;
	/** ID del purchase order (opzionale) */
	purchaseOrderId?: number | null;
	/** Se copiare anche i requisiti */
	copyRequirements: boolean;
}

/**
 * Schema Zod per la validazione runtime di JobOrder
 * Basato su formato PascalCase del backend
 */
export const JobOrderSchema = z.object({
	// Proprietà base da BaseEntity
	id: z.number(),
	createdAt: z.string().optional(), // Può essere undefined in alcuni contesti API
	createdBy: z.string().optional(), // Può essere undefined in alcuni contesti API
	modifiedAt: z.string().nullish(), // Può essere null nel JSON
	modifiedBy: z.string().nullish(), // Può essere null nel JSON

	// Proprietà scalari principali con validazioni dal C#
	orderType: z.number().optional(), // Può essere undefined in alcuni contesti
	taskId: z.number().nullish(), // nullable
	code: z.string().min(1).max(255), // Required, MaxLength(255)
	name: z.string().min(1), // Required
	customerId: z.number().optional(), // Può essere undefined in alcuni contesti
	purchaseOrderId: z.number().nullish(), // nullable
	startDate: z.string().nullish(), // DateTime nullable
	endDate: z.string().nullish(), // DateTime nullable
	budgetHours: z.number().min(0).nullish(), // decimal(18,4) nullable, Range(0.0, double.MaxValue)
	approvedHours: z.number().min(0).nullish(), // decimal(18,4) nullable, Range(0.0, double.MaxValue)
	notes: z.string().nullish(), // nullable
	isRequirementRequired: z.boolean().optional(), // Può essere undefined in alcuni contesti
	isHolidayJobOrder: z.boolean().optional(), // Può essere undefined in alcuni contesti

	// Navigation properties singole (z.any() per compatibilità Vue con $expand)
	customer: z.any().optional(),
	task: z.any().optional(),
	purchaseOrder: z.any().optional(),

	// Navigation properties collezioni (z.any() per compatibilità Vue con $expand)
	activities: z.array(z.any()).optional(),
	managers: z.array(z.any()).optional(),
	revenues: z.array(z.any()).optional(),
	requirements: z.array(z.any()).optional(),
	allowedWorkers: z.array(z.any()).optional(),
	allowedGroupWorkers: z.array(z.any()).optional(),
	allowedGroups: z.array(z.any()).optional(),
	timecardEntries: z.array(z.any()).optional(),
});

/**
 * Schema Zod per la validazione di CopyOrderParameters
 */
export const CopyOrderParametersSchema = z.object({
	orderId: z.number(),
	purchaseOrderId: z.number().nullish(), // nullable
	copyRequirements: z.boolean(),
});
